/**
 * ClientDigestPreview — Inline preview of the next Daily Client Digest
 * email for a given tenant. Lists every client user on the tenant, lets
 * an admin (a) preview the rendered HTML in an iframe, or (b) fire the
 * preview email to that user's inbox right now.
 *
 * Lives on the Client Overview tab. Platform-admin / delivery-admin
 * only — the parent page gates the render.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Envelope, Eye, PaperPlaneRight } from "@phosphor-icons/react";
import { safeSrcDoc } from "@/lib/safeHtml";

export default function ClientDigestPreview({ tenantId, tenantName }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(null); // { email, html, subject, counts }
  const [sending, setSending] = useState(null);       // email currently being sent

  // Detect whether we're looking at the preview pod (preview.emergentagent.com
  // hostname) — if so, the backend's outbound-email safety gate will
  // intercept any "Send to inbox" click. Warn the admin up front so they
  // don't waste a click expecting an email that won't arrive.
  const onPreviewPod = typeof window !== "undefined"
    && window.location.hostname.includes("preview.emergentagent.com");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/users?tenant_id=${tenantId}&user_type=client`)
      .then(res => { if (alive) setClients(res.data || []); })
      .catch(() => { if (alive) toast.error("Failed to load client users"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tenantId]);

  const openPreview = async (u) => {
    try {
      const res = await api.get(`/client-digest/preview-html`, {
        params: { recipient: u.email },
      });
      setPreviewing({
        email: u.email,
        name: u.name,
        subject: res.data.subject,
        html: res.data.html,
        counts: res.data.section_counts,
        optedIn: res.data.user_opted_in,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to render preview");
    }
  };

  const sendPreview = async (u) => {
    // Prompt with the recipient pre-filled so the user can confirm or
    // override the destination before sending. Pressing Cancel aborts.
    const sendTo = window.prompt(
      `Send a [PREVIEW] of today's Daily Digest.\n\n` +
      `Default recipient is the client user's address — you can change it ` +
      `below (e.g. send it to your own inbox first). The digest content is ` +
      `always rendered from ${u.name}'s perspective regardless of the To: ` +
      `address.`,
      u.email,
    );
    if (sendTo === null) return;  // Cancel
    const cleaned = (sendTo || "").trim();
    if (!cleaned || !cleaned.includes("@")) {
      toast.error("Send aborted — that doesn't look like a valid email.");
      return;
    }
    setSending(u.email);
    try {
      const res = await api.post(`/client-digest/preview`, null, {
        params: { recipient: u.email, send_to: cleaned },
      });
      // Honest status reporting — the preview pod's outbound-safety
      // gate (built after the cold-outreach link leak) silently
      // refuses to send from this pod. Distinguish "actually
      // delivered" from "intercepted on purpose" so admins aren't
      // confused when the inbox stays empty.
      const status = res.data.delivery_status;
      const where = res.data.was_redirected
        ? `${cleaned} (redirected from ${u.email})`
        : cleaned;
      if (status === "sent") {
        toast.success(`Preview sent to ${where}`);
      } else if (status === "blocked_preview_pod") {
        toast.warning(
          `Preview NOT sent — the preview pod's outbound-email safety gate ` +
          `intercepted it. To actually send, either (a) do this from the ` +
          `production site (eyvopulse.com) which fires real emails, OR ` +
          `(b) set EMAIL_OUTBOUND_FROM_PREVIEW=true on the preview pod.`,
          { duration: 15000 },
        );
      } else if (status === "blocked_safe_mode") {
        toast.warning(
          `Preview NOT sent — EMAIL_SAFE_MODE=true is blocking all outbound ` +
          `email on this pod. Unset it (or do this from production) to send.`,
          { duration: 15000 },
        );
      } else if (status === "send_failed") {
        toast.error(`Send failed — check the backend logs for the SendGrid response.`);
      } else {
        toast.success(`Preview sent to ${where}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send preview");
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="border border-[#E5E5E5] bg-white" data-testid="client-digest-preview">
      <div className="px-5 py-4 border-b border-[#E5E5E5] bg-[#FAFAFA]">
        <div className="flex items-center gap-2">
          <Envelope size={16} className="text-[#0055FF]" weight="bold" />
          <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#0A0A0A]">
            Daily Digest Preview
          </h3>
        </div>
        <p className="text-[11px] text-[#737373] mt-1 leading-snug">
          Render or send a one-off preview of the next Daily Digest email for any
          client user on <strong>{tenantName}</strong>. Showstoppers appear at the
          top in red, then tasks where the ball is with the client.
        </p>
      </div>
      <div className="p-5">
        {onPreviewPod && (
          <div
            className="mb-4 border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-[11px] text-[#92400E] leading-snug"
            data-testid="preview-pod-warning"
          >
            <strong className="font-bold uppercase tracking-wider text-[10px]">
              ⚠ You're on the preview environment
            </strong>
            <span className="block mt-0.5">
              "Send to inbox" will be intercepted by the outbound-email
              safety gate and no email will actually be delivered. To send
              a real preview, do this from the production site
              (<a href="https://eyvopulse.com" className="underline">eyvopulse.com</a>).
              The "Preview" button still works fine here.
            </span>
          </div>
        )}
        {loading && <p className="text-xs text-[#A3A3A3]">Loading client users…</p>}
        {!loading && clients.length === 0 && (
          <p className="text-xs text-[#A3A3A3]">No client users on this tenant yet.</p>
        )}
        {!loading && clients.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E5E5] text-[10px] font-bold uppercase tracking-[0.1em] text-[#A3A3A3]">
                <th className="text-left py-2">Name</th>
                <th className="text-left py-2">Email</th>
                <th className="text-left py-2">Role</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(u => (
                <tr key={u.id} className="border-b border-[#F5F5F5]" data-testid={`digest-recipient-${u.id}`}>
                  <td className="py-2.5 text-[#0A0A0A]">{u.name}</td>
                  <td className="py-2.5 text-[#525252] text-xs">{u.email}</td>
                  <td className="py-2.5 text-[#737373] text-[11px] uppercase">{(u.role || "").replace(/_/g, " ")}</td>
                  <td className="py-2.5 text-right">
                    <Button
                      onClick={() => openPreview(u)}
                      variant="outline"
                      className="rounded-none border-[#E5E5E5] h-8 text-[11px] font-bold uppercase tracking-wider mr-2"
                      data-testid={`digest-preview-${u.id}`}
                    >
                      <Eye size={12} className="mr-1" /> Preview
                    </Button>
                    <Button
                      onClick={() => sendPreview(u)}
                      disabled={sending === u.email}
                      className="bg-[#0055FF] hover:bg-[#0040CC] text-white rounded-none h-8 text-[11px] font-bold uppercase tracking-wider"
                      data-testid={`digest-send-${u.id}`}
                      title={`Send a [PREVIEW] copy of today's digest to ${u.email}. You'll be prompted to confirm or change the destination email before it's sent — useful if you want to route the preview to your own inbox first.`}
                    >
                      <PaperPlaneRight size={12} className="mr-1" />
                      {sending === u.email ? "Sending…" : "Send to inbox"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!previewing} onOpenChange={() => setPreviewing(null)}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-3xl w-[92vw] p-0 gap-0 max-h-[90vh] flex flex-col">
          <DialogHeader className="px-5 py-3.5 border-b border-[#E5E5E5] shrink-0">
            <DialogTitle className="font-bold tracking-tight text-base flex items-center gap-2">
              <Envelope size={16} className="text-[#0055FF]" weight="bold" />
              Preview · {previewing?.name} ({previewing?.email})
            </DialogTitle>
            {previewing && (
              <div className="flex items-center gap-2 mt-2 flex-wrap text-[10px]">
                <span className="px-2 py-0.5 bg-[#F5F5F5] text-[#525252] font-bold uppercase">{previewing.subject}</span>
                {!previewing.optedIn && (
                  <span className="px-2 py-0.5 bg-[#FFFBEB] text-[#92400E] font-bold uppercase">
                    ⚠ Opted out — they won't receive scheduled digests
                  </span>
                )}
                <span className="px-1.5 py-0.5 bg-[#FEE2E2] text-[#991B1B] font-bold uppercase">
                  🚩 Showstoppers: {previewing.counts.showstoppers}
                </span>
                <span className="px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] font-bold uppercase">
                  Ball-with-client: {previewing.counts.ball_with_client}
                </span>
                <span className="px-1.5 py-0.5 bg-[#DBEAFE] text-[#1E40AF] font-bold uppercase">
                  Tickets: {previewing.counts.open_tickets}
                </span>
                <span className="px-1.5 py-0.5 bg-[#EDE9FE] text-[#5B21B6] font-bold uppercase">
                  In progress: {previewing.counts.in_progress}
                </span>
                <span className="px-1.5 py-0.5 bg-[#DCFCE7] text-[#166534] font-bold uppercase">
                  Completed 24h: {previewing.counts.recently_completed}
                </span>
              </div>
            )}
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-auto bg-[#F5F5F5]">
            {previewing && (
              <iframe
                title="digest-preview"
                srcDoc={safeSrcDoc(previewing.html)}
                sandbox=""
                className="w-full h-full min-h-[60vh] border-0"
                data-testid="digest-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
